import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axios";
import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Spinner,
    useToast,
    Box,
    Badge,
    Switch,
    VStack,
    HStack,
    FormControl,
    FormLabel,
    Input,
    Button,
    Checkbox,
    Text,
    Divider,
} from "@chakra-ui/react";
import { FaCheck, FaTimes } from "react-icons/fa";

const EditStoreCouponsTable = ({ id }) => {
    const toast = useToast();
    const [isLoading, setIsLoading] = useState(true);
    const [couponsData, setCouponsData] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Form state
    const [formData, setFormData] = useState({
        coupon_code: '',
        coupon_amount: '',
        free_shipping: false,
        minimum_amount: '',
        expiry_date: '',
        coupon_display: ''
    });

    useEffect(() => {
        const fetchCoupons = async () => {
            setIsLoading(true);
            try {
                const response = await axiosInstance.post(
                    `/dummy/get-store-coupons`,
                    { hstore_id: id }
                );
                
                // Assuming the coupons data is in response.data.data
                // If the structure is different, adjust accordingly
                const coupons = response.data.data || [];
                setCouponsData(coupons);
                setIsLoading(false);
            } catch (error) {
                console.error("Error fetching coupons:", error);
                toast({
                    title: "Error",
                    description: "Failed to fetch coupons data",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
                setIsLoading(false);
            }
        };

        if (id) {
            fetchCoupons();
        }
    }, [id, toast]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmitCoupon = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Format the expiry date to match the required format
            const expiryDate = new Date(formData.expiry_date);
            const formattedExpiryDate = expiryDate.toISOString().slice(0, 19) + '+00';

            const payload = {
                hstore_id: parseInt(id),
                coupon_code: formData.coupon_code,
                coupon_type: "percent",
                coupon_amount: parseFloat(formData.coupon_amount),
                free_shipping: formData.free_shipping,
                minimum_amount: parseFloat(formData.minimum_amount),
                expiry_date: formattedExpiryDate,
                coupon_display: formData.coupon_display
            };

            const response = await axiosInstance.post('/dummy/add-store-coupons', payload);

            toast({
                title: 'Success',
                description: 'Coupon added successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            // Reset form
            setFormData({
                coupon_code: '',
                coupon_amount: '',
                free_shipping: false,
                minimum_amount: '',
                expiry_date: '',
                coupon_display: ''
            });

            // Refresh the coupons list
            const refreshResponse = await axiosInstance.post(
                `/dummy/get-store-coupons`,
                { hstore_id: id }
            );
            const coupons = refreshResponse.data.data || [];
            setCouponsData(coupons);

        } catch (error) {
            console.error('Error adding coupon:', error);
            toast({
                title: 'Error',
                description: 'Failed to add coupon',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderBooleanIcon = (value) => {
        return value ? <FaCheck color="green" /> : <FaTimes color="red" />;
    };

    const renderYesNo = (value) => {
        return value ? 'Yes' : 'No';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const options = {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        };
        return date.toLocaleDateString('en-GB', options);
    };

    const handleToggleActive = async (hcouponId, currentStatus) => {
        try {
            const newStatus = !currentStatus;
            
            const response = await axiosInstance.post('/dummy/toggle-store-coupon-active', {
                hcoupon_id: hcouponId,
                active_yn: newStatus
            });
            
            // Update the local state to reflect the change
            setCouponsData(prevCoupons => 
                prevCoupons.map(coupon => 
                    coupon.hcoupon_id === hcouponId 
                        ? { ...coupon, activeyn: newStatus }
                        : coupon
                )
            );
            
            toast({
                title: 'Success',
                description: 'Coupon status updated successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Error toggling coupon status:', error);
            toast({
                title: 'Error',
                description: 'Failed to update coupon status',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleToggleShowOnStore = async (hcouponId, currentStatus) => {
        try {
            const newStatus = !currentStatus;
            
            const response = await axiosInstance.post('/dummy/toggle-store-coupon-show-active', {
                hcoupon_id: hcouponId,
                active_yn: newStatus
            });
            
            // Update the local state to reflect the change
            setCouponsData(prevCoupons => 
                prevCoupons.map(coupon => 
                    coupon.hcoupon_id === hcouponId 
                        ? { ...coupon, show_on_store: newStatus }
                        : coupon
                )
            );
            
            toast({
                title: 'Success',
                description: 'Show on store status updated successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Error toggling show on store status:', error);
            toast({
                title: 'Error',
                description: 'Failed to update show on store status',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <Spinner size="lg" />
            </Box>
        );
    }

    return (
        <VStack spacing={6} align="stretch">
            {/* Add Coupon Form */}
            <Box p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
                <Text fontSize="lg" fontWeight="bold" mb={4}>Add New Coupon</Text>
                <form onSubmit={handleSubmitCoupon}>
                    <VStack spacing={4} align="stretch">
                        <HStack spacing={4}>
                            <FormControl isRequired>
                                <FormLabel>Code</FormLabel>
                                <Input
                                    value={formData.coupon_code}
                                    onChange={(e) => handleInputChange('coupon_code', e.target.value)}
                                    placeholder="Enter coupon code"
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Type</FormLabel>
                                <Input value="Percent" isReadOnly bg="gray.100" />
                            </FormControl>
                        </HStack>
                        
                        <HStack spacing={4}>
                            <FormControl isRequired>
                                <FormLabel>Amount</FormLabel>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={formData.coupon_amount}
                                    onChange={(e) => handleInputChange('coupon_amount', e.target.value)}
                                    placeholder="Enter amount"
                                />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel>Minimum Amount</FormLabel>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={formData.minimum_amount}
                                    onChange={(e) => handleInputChange('minimum_amount', e.target.value)}
                                    placeholder="Enter minimum amount"
                                />
                            </FormControl>
                        </HStack>
                        
                        <HStack spacing={4}>
                            <FormControl isRequired>
                                <FormLabel>Expiry</FormLabel>
                                <Input
                                    type="date"
                                    value={formData.expiry_date}
                                    onChange={(e) => handleInputChange('expiry_date', e.target.value)}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Free Shipping</FormLabel>
                                <Checkbox
                                    isChecked={formData.free_shipping}
                                    onChange={(e) => handleInputChange('free_shipping', e.target.checked)}
                                >
                                    Yes
                                </Checkbox>
                            </FormControl>
                        </HStack>
                        
                        <FormControl isRequired>
                            <FormLabel>Display Name</FormLabel>
                            <Input
                                value={formData.coupon_display}
                                onChange={(e) => handleInputChange('coupon_display', e.target.value)}
                                placeholder="Enter display name"
                            />
                        </FormControl>
                        
                        <Button
                            type="submit"
                            colorScheme="blue"
                            isLoading={isSubmitting}
                            loadingText="Adding Coupon..."
                        >
                            Add Coupon
                        </Button>
                    </VStack>
                </form>
            </Box>
            
            <Divider />
            
            {/* Coupons List */}
            {!couponsData || couponsData.length === 0 ? (
                <Box p={4} textAlign="center">
                    No coupons found for this store.
                </Box>
            ) : (
                <Box overflowX="auto">
                    <Table variant="striped" size="sm">
                        <Thead>
                            <Tr>
                                <Th>Author</Th>
                                <Th>Code</Th>
                                <Th>Type</Th>
                                <Th>Amount</Th>
                                <Th>Free Shipping</Th>
                                <Th>Minimum Amount</Th>
                                <Th>Expires</Th>
                                <Th>Show on Store YN</Th>
                                <Th>Coupon Display</Th>
                                <Th>Active YN</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {couponsData.map((coupon, index) => (
                                <Tr key={index}>
                                    <Td>{coupon.addedby || 'N/A'}</Td>
                                    <Td>{coupon.coupon_code || 'N/A'}</Td>
                                    <Td>{coupon.coupon_type || 'N/A'}</Td>
                                    <Td>{coupon.coupon_amount || 'N/A'}</Td>
                                    <Td>{renderYesNo(coupon.free_shipping_yn)}</Td>
                                    <Td>{coupon.minimum_amount || 'N/A'}</Td>
                                    <Td>{formatDate(coupon.date_expires)}</Td>
                                    <Td>
                                        <Switch
                                            isChecked={coupon.show_on_store}
                                            onChange={() => handleToggleShowOnStore(coupon.hcoupon_id, coupon.show_on_store)}
                                            colorScheme="green"
                                        />
                                    </Td>
                                    <Td>{coupon.coupon_display || 'N/A'}</Td>
                                    <Td>
                                        <Switch
                                            isChecked={coupon.activeyn}
                                            onChange={() => handleToggleActive(coupon.hcoupon_id, coupon.activeyn)}
                                            colorScheme="green"
                                        />
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </Box>
            )}
        </VStack>
    );
};

export default EditStoreCouponsTable; 